import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

// ── Fuente única de verdad del ID de medición ──
const MEASUREMENT_ID = 'G-52LBJE35JC'
const CANONICAL_ORIGIN = 'https://alinova.co'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

let gaInitialized = false

/**
 * Inicializa Google Analytics 4 (gtag.js) UNA ÚNICA VEZ para toda la
 * aplicación. Este proyecto no tiene un index.html editable, así que
 * este módulo cumple el mismo rol que el snippet oficial de Google en
 * el <head>: debe invocarse de forma SÍNCRONA en el punto de entrada
 * real (index.tsx), ANTES de montar <App />. Esto elimina cualquier
 * dependencia del ciclo de efectos de React para la inicialización —
 * solo queda un único mecanismo de arranque, ejecutado una sola vez
 * por el propio módulo de JavaScript.
 */
export function initGoogleAnalytics() {
  if (gaInitialized) return
  if (typeof window === 'undefined') return

  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    script.onerror = () => {
      // Visible en consola si el script de Google es bloqueado por un
      // ad-blocker, una política de red o un CSP — sin esto, un fallo
      // de carga era indetectable desde el código.
      console.warn(
        '[GA4] No se pudo cargar gtag.js. Posible bloqueo de red, extensión del navegador o política de seguridad.',
      )
    }
    document.head.appendChild(script)
  }

  // Patrón estándar de gtag.js: dataLayer.push(arguments) encolado
  // ANTES de que el script remoto termine de cargar. Al cargar, gtag.js
  // procesa retroactivamente todo lo ya encolado.
  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args)
    }

  window.gtag('js', new Date())
  // send_page_view en false: las vistas de página se envían manualmente
  // por cambio real de ruta (ver useGoogleAnalyticsPageViews), ya que
  // HashRouter no produce recargas de documento que gtag.js detecte
  // por sí solo.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false })

  gaInitialized = true
}

/**
 * Envía un evento page_view por cada ruta real de React Router
 * (incluida la carga inicial), sin duplicar envíos por el mismo
 * cambio de ruta. Requiere que initGoogleAnalytics() ya se haya
 * ejecutado (ocurre en index.tsx antes de montar la app). Debe
 * invocarse dentro de un componente montado dentro del Router.
 */
export function useGoogleAnalyticsPageViews() {
  const location = useLocation()
  const lastSentPath = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window.gtag !== 'function') return

    const routePath = `${location.pathname}${location.search}`
    if (lastSentPath.current === routePath) return
    lastSentPath.current = routePath

    // HashRouter no recarga el documento entre rutas: se construye
    // manualmente una URL coherente con el dominio canónico publicado
    // y la ruta con hash real, sin datos personales en los parámetros.
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: `${CANONICAL_ORIGIN}/#${routePath}`,
      page_path: routePath,
    })
  }, [location.pathname, location.search])
}
